import { NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs';
import { ActivityType, IKvPair } from '@curbnturf/entities';
import { EnumSelectorPipe, IActivityForm, ListingForm } from '@curbnturf/form-helpers';
import { DisplayValidationErrorsDirective } from '@curbnturf/form-helpers/src/lib/validation-errors/display-validation-errors/display-validation-errors.directive';

@Component({
  selector: 'curbnturf-onsite-activities',
  templateUrl: './onsite-activities.component.html',
  styleUrls: ['./onsite-activities.component.scss'],
  standalone: true,
  imports: [
    DisplayValidationErrorsDirective,
    FormsModule,
    NgIf,
    NgFor,
    NgClass,
    ReactiveFormsModule,
    SiteSvgsComponent,
  ],
})
export class OnsiteActivitiesComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() activities: FormArray<FormGroup<IActivityForm>>;
  @Input() showCustom: boolean;
  @Input() showDescriptions: boolean = true;

  activityTypesArray: { key: string; value: string }[];

  listingFormObject: ListingForm;

  expanded: (boolean | undefined)[] = [];

  get customActivities() {
    return this.activities ? this.activities.controls.filter((activity) => activity.value.type === 'custom') : [];
  }

  get customActivityTypes(): string[] {
    return this.customActivities.map((activity) => activity.value.typeCustom || '');
  }

  constructor(private fb: FormBuilder) {
    this.listingFormObject = new ListingForm(fb);
  }

  ngAfterViewInit() {
    if (this.activities) {
      this.activities.valueChanges.subscribe(() => {
        this.expanded = this.activityTypesArray.map((activityType) => {
          const activityForm = this.activities.controls.find(this.match(activityType.key)) as FormGroup<IActivityForm>;

          if (activityForm) {
            return !!activityForm.value.description;
          }

          return false;
        });
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.activities) {
      this.activityTypesArray = [
        ...new EnumSelectorPipe().transform(ActivityType).filter((activity) => activity.key !== 'custom'),
      ];
    }
  }

  ngOnDestroy() {
    if (this.activities) {
      this.activities.clearValidators();
    }
  }

  selected(activity: string): boolean {
    if (this.activities) {
      return !!this.activities.controls.find(this.match(activity));
    }

    return false;
  }

  selectedCustom(activity: string): boolean {
    if (this.activities) {
      return !!this.activities.controls.find(this.matchCustom(activity));
    }

    return false;
  }

  toggle(activityType: IKvPair) {
    if (this.selected(activityType.key)) {
      this.activities.removeAt(this.activities.controls.findIndex(this.match(activityType.key)));
    } else {
      this.activities.push(this.fb.group(this.listingFormObject.activityGroup(activityType)));
    }
    this.activities.markAsDirty();
  }

  toggleCustom(customType: string) {
    if (this.selectedCustom(customType)) {
      this.activities.removeAt(this.activities.controls.findIndex(this.matchCustom(customType)));
    } else {
      this.enableCustom(customType);
    }

    this.activities.markAsDirty();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addCustom(event: any) {
    this.enableCustom(event.target.value);
    event.target.value = '';
  }

  enableCustom(customType: string) {
    const customActivity = this.fb.group(this.listingFormObject.activityGroup());
    customActivity.get('type')?.setValue('custom');
    customActivity.get('typeCustom')?.setValue(customType);
    this.activities.push(customActivity);

    this.activities.markAsDirty();
  }

  expand(index: number) {
    this.expanded[index] = true;
  }

  description(activity: string): FormControl<string | null> | undefined {
    const activityForm = this.activities.controls.find(this.match(activity)) as FormGroup<IActivityForm>;

    if (activityForm) {
      return activityForm.controls.description;
    }

    return;
  }

  descriptionCustom(activity: string): FormControl<string | null> | undefined {
    const activityForm = this.activities.controls.find(this.matchCustom(activity)) as FormGroup<IActivityForm>;

    if (activityForm) {
      return activityForm.controls.description;
    }

    return;
  }

  private match(activity: string): (form: FormGroup<IActivityForm>) => boolean {
    return (activityValue) => activityValue.value.type === activity;
  }

  private matchCustom(customType: string): (form: FormGroup<IActivityForm>) => boolean {
    return (activityValue) => activityValue.value.type === 'custom' && activityValue.value.typeCustom === customType;
  }
}
